import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  zIndex: 22000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "14px 12px",
};

const shellStyle = {
  width: "100%",
  maxWidth: "560px",
  borderRadius: "16px",
  padding: "28px",
  background: "var(--erp-modal-bg, #0e1730)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
  borderTop: "3px solid #3DB6E9",
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100dvh - 28px)",
  overflowY: "auto",
};

const contentWrapperStyle = {};

const sectionStyle = {
  marginTop: "18px",
  borderRadius: "16px",
  padding: "18px",
  background: "var(--erp-modal-section, #121f3d)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 800,
  color: "var(--erp-modal-title, #f2f6ff)",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.78rem",
  color: "var(--erp-modal-muted, #aebbd0)",
};

const fieldGridStyle = {
  marginTop: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const labelStyle = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--erp-modal-text, #dfe8f7)",
  marginBottom: "4px",
  display: "block",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input, #152344)",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  color: "var(--erp-modal-text, #dfe8f7)",
  fontSize: "0.95rem",
};

const actionsRowStyle = {
  marginTop: "16px",
  display: "flex",
  gap: "12px",
};

const buttonBase = {
  cursor: "pointer",
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  flex: 1,
  border: "none",
};

const destructiveButtonStyle = {
  ...buttonBase,
  border: "1px solid var(--erp-modal-danger, #9B3445)",
  background: "var(--erp-modal-danger, #9B3445)",
  color: "#ffffff",
};

const secondaryButtonStyle = {
  ...buttonBase,
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  background: "var(--erp-modal-bg, #0e1730)",
  color: "var(--erp-modal-text, #dfe8f7)",
};

const toastOverlayStyle = {
  position: "fixed",
  top: "14px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 25000,
};

const toastCardStyle = {
  background: "var(--erp-modal-primary, #223A7A)",
  border: "1px solid #3DB6E9",
  borderRadius: "18px",
  padding: "14px 24px",
  color: "#ffffff",
  fontWeight: 700,
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  maxWidth: "420px",
  width: "min(90vw, 420px)",
  textAlign: "center",
};

const savingOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 23000,
  pointerEvents: "none",
};

function toLocalISODate(value) {
  const d = new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildDateRange(startValue, endValue) {
  const start = new Date(`${String(startValue).slice(0, 10)}T00:00:00`);
  const end = new Date(`${String(endValue).slice(0, 10)}T00:00:00`);
  const out = [];

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(toLocalISODate(d));
  }

  return out;
}

function getRatePriceDay(ratePrice) {
  return String(ratePrice?.stay_date || ratePrice?.start_date || "").slice(0, 10);
}

export default function RemoveRatePricesPopup({ show, onClose, formData }) {
  const [deleteStart, setDeleteStart] = useState("");
  const [deleteEnd, setDeleteEnd] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
      }
    };
  }, []);

  if (!show) return null;

  const handleToast = (message, closeAfter = true) => {
    if (toastTimer.current) {
      clearTimeout(toastTimer.current);
    }
    setToastMessage(message);
    if (closeAfter) {
      toastTimer.current = setTimeout(() => {
        setToastMessage("");
        onClose && onClose();
      }, 2000);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!deleteStart || !deleteEnd) {
      alert("Select a valid date range to delete.");
      return;
    }
    const roomRateId = formData.room_rate?.id || formData.room_rate_id;
    const expectedDays = buildDateRange(deleteStart, deleteEnd);

    const verifyAppliedAfterTimeout = async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rate-prices/history/${roomRateId}`,
        { withCredentials: true }
      );

      const freshRatePrices = Array.isArray(data) ? data : [];
      const remainingDays = freshRatePrices
        .filter((row) => String(row.room_rate_id) === String(roomRateId))
        .map((row) => getRatePriceDay(row))
        .filter((day) => expectedDays.includes(day));

      return remainingDays.length === 0;
    };

    setIsDeleting(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rate-prices/delete-range`,
        {
          room_rate_id: roomRateId,
          start_date: deleteStart,
          end_date: deleteEnd,
        },
        { withCredentials: true }
      );
      setIsDeleting(false);
      handleToast(data?.message || "✅ Rate prices removed successfully.");
      setDeleteStart("");
      setDeleteEnd("");
    } catch (error) {
      const status = error?.response?.status;
      if (status === 504) {
        try {
          const alreadyApplied = await verifyAppliedAfterTimeout();
          if (alreadyApplied) {
            setIsDeleting(false);
            handleToast("Rate prices removed successfully.");
            setDeleteStart("");
            setDeleteEnd("");
            return;
          }
        } catch (verificationError) {
          console.error("Error verifying rate price removal after timeout:", verificationError);
        }
      }
      console.error("Error deleting rate prices:", error);
      alert(error?.response?.data?.error || "Error deleting rate prices.");
      setIsDeleting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={shellStyle}>
        <div style={{ textAlign: "center", marginBottom: "18px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "var(--erp-modal-muted, #aebbd0)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            Room rate studio
          </p>
          <h2 style={{ margin: 0, fontSize: "1.35rem", color: "var(--erp-modal-title, #f2f6ff)" }}>Remove rate prices</h2>
          <p style={{ marginTop: "6px", color: "var(--erp-modal-muted, #aebbd0)" }}>
            Deletes every price + min/max stay entry for each selected day.
          </p>
        </div>

        <div style={contentWrapperStyle}>
          <form onSubmit={handleSubmit}>
            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Select range</h3>
                  <p style={sectionTextStyle}>
                    The backend removes each row for the room rate (including min/max stay) between the bounds.
                  </p>
                </div>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>From</label>
                  <input
                    type="date"
                    style={inputFieldStyle}
                    value={deleteStart}
                    onChange={(e) => setDeleteStart(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>To</label>
                  <input
                    type="date"
                    style={inputFieldStyle}
                    value={deleteEnd}
                    onChange={(e) => setDeleteEnd(e.target.value)}
                    required
                  />
                </div>
              </div>
            </section>

            <div style={actionsRowStyle}>
              <button type="submit" style={destructiveButtonStyle} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete prices"}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={() => onClose && onClose()}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {isDeleting && <div style={savingOverlayStyle}>Deleting rate prices…</div>}

      {toastMessage && (
        <div style={toastOverlayStyle}>
          <div style={toastCardStyle}>{toastMessage}</div>
        </div>
      )}
    </div>
  );
}
